import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform, useReducedMotion } from "framer-motion";
import {
  ArrowLeft,
  ArrowRight,
  ArrowUpRight,
  MapPin,
  Clock,
  Building2,
  CalendarDays,
  Paperclip,
  Check,
  AlertCircle,
  X,
  Loader2,
} from "lucide-react";
import Nav from "@/components/thoratech/Nav";
import Footer from "@/components/thoratech/Footer";
import Seo from "@/seo/Seo";
import { BRAND, CAREERS } from "@/content/thoratech";

const API_BASE = process.env.REACT_APP_BACKEND_URL;
const ACCEPT_EXT = ".pdf,.doc,.docx";
const ACCEPT_MIME = [
  "application/pdf",
  "application/msword",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
];
const MAX_BYTES = 5 * 1024 * 1024;

// GTM push — event only, never PII.
function trackApplicationSubmit() {
  try {
    if (typeof window !== "undefined") {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({ event: "job_application_submit" });
    }
  } catch (_e) {
    /* GTM optional */
  }
}

// Branded fallback shown only when the API response cannot be interpreted.
const SUBMIT_FALLBACK_MESSAGE =
  "We could not submit your application. Please try again. " +
  "If the issue continues, email your CV to hello@thoratech.global.";

// FastAPI returns `detail` as either a string (app-thrown HTTPException) or
// an array of Pydantic error objects (validation on Form/EmailStr/min_length).
// Normalise both into a single readable sentence for the candidate.
function readableApiError(payload) {
  if (!payload) return SUBMIT_FALLBACK_MESSAGE;
  const d = payload.detail;
  if (typeof d === "string" && d.trim()) return d.trim();
  if (Array.isArray(d) && d.length > 0) {
    const first = d[0] || {};
    // Pydantic loc is like ["body", "email"]; pick the last string segment.
    const loc = Array.isArray(first.loc) ? first.loc : [];
    const field = loc.slice(1).reverse().find((x) => typeof x === "string");
    if (field === "email") return "Please enter a valid email address.";
    if (field === "phone") return "Please enter a valid phone number.";
    if (field === "name")  return "Please enter your full name.";
    if (field === "role")  return "Please pick a listed role.";
    if (field === "resume") return "Please attach a valid resume file.";
    if (typeof first.msg === "string" && first.msg.trim()) return first.msg.trim();
  }
  return SUBMIT_FALLBACK_MESSAGE;
}

// Split a headline into words so we can mask + stagger each word.
function useWords(text) {
  return useMemo(() => text.split(/(\s+)/), [text]);
}

function RevealWord({ children, delay = 0 }) {
  const reduced = useReducedMotion();
  if (children.trim() === "") return <span>{children}</span>;
  return (
    <span className="inline-block overflow-hidden align-baseline pb-[0.08em]">
      <motion.span
        className="inline-block will-change-transform"
        initial={reduced ? { y: 0, opacity: 1 } : { y: "110%", opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ delay, duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
      >
        {children}
      </motion.span>
    </span>
  );
}

function useDataLayer() {
  useEffect(() => {
    // Idempotent; safe if GTM already pushed a pageview.
    if (typeof window !== "undefined") {
      window.dataLayer = window.dataLayer || [];
    }
  }, []);
}

export default function Careers() {
  useDataLayer();

  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: heroRef,
    offset: ["start start", "end start"],
  });
  const heroY = useTransform(scrollYProgress, [0, 1], [0, -60]);
  const heroOpacity = useTransform(scrollYProgress, [0, 1], [1, 0.35]);

  const [selectedRole, setSelectedRole] = useState("");
  const [openRoleId, setOpenRoleId] = useState(null);

  const applyFor = (title) => {
    setSelectedRole(title);
    // Scroll to form after state settles
    requestAnimationFrame(() => {
      const el = document.getElementById("apply");
      if (el) {
        const top = el.getBoundingClientRect().top + window.scrollY - 100;
        window.scrollTo({ top, behavior: "smooth" });
      }
    });
  };

  const titleWords = useWords(CAREERS.intro.title);
  const accentWords = useWords(CAREERS.intro.titleAccent);

  const marquee = [
    "Balewadi, Pune",
    "6 Days a week",
    "Onsite operations centre",
    "Governance from day one",
    "Outcomes over hours",
    "Founder-led",
  ];

  return (
    <>
      <Nav />
      <Seo route="/careers" />

      {/* ============================ HERO ============================ */}
      <main data-testid="careers-page" className="bg-white text-[#01080E]">
        <section
          ref={heroRef}
          className="relative pt-[130px] md:pt-[170px] pb-24 md:pb-32 overflow-hidden"
        >
          {/* Grid + spotlight */}
          <div aria-hidden className="pointer-events-none absolute inset-0">
            <div className="absolute inset-0 grid-texture-light opacity-[0.55]" />
            <motion.div
              style={{ y: heroY, opacity: heroOpacity }}
              className="absolute -top-32 -right-40 w-[720px] h-[720px] rounded-full blur-[120px] bg-[#2AC0A5]/25"
            />
            <div className="absolute inset-x-0 bottom-0 h-px bg-gradient-to-r from-transparent via-[#01080E]/10 to-transparent" />
          </div>

          <div className="relative max-w-7xl mx-auto px-6 md:px-12">
            <Link
              to="/"
              data-testid="careers-back-link"
              className="inline-flex items-center gap-2 text-sm font-medium text-[#01080E]/70 hover:text-[#2AC0A5] transition-colors mb-12"
            >
              <ArrowLeft size={16} /> Back to home
            </Link>

            <motion.div
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="inline-flex items-center gap-3 mb-8"
            >
              <span className="block h-px w-10 bg-[#2AC0A5]" />
              <span
                data-testid="careers-eyebrow"
                className="font-mono-tech text-[11px] font-semibold uppercase tracking-[0.28em] text-[#01080E]"
              >
                {CAREERS.intro.eyebrow}
              </span>
            </motion.div>

            <h1
              data-testid="careers-title"
              aria-label={`${CAREERS.intro.title} ${CAREERS.intro.titleAccent}`}
              className="font-display font-extrabold leading-[0.98] tracking-[-0.02em] text-[#01080E] text-[13vw] sm:text-[10vw] lg:text-[8.2vw] xl:text-[7.4vw]"
            >
              <span className="block" aria-hidden="true">
                {titleWords.map((w, i) => (
                  <RevealWord key={`t-${i}`} delay={0.05 + i * 0.05}>
                    {w}
                  </RevealWord>
                ))}
              </span>
              <span className="block text-[#2AC0A5]" aria-hidden="true">
                {accentWords.map((w, i) => (
                  <RevealWord key={`a-${i}`} delay={0.35 + i * 0.05}>
                    {w}
                  </RevealWord>
                ))}
              </span>
            </h1>

            <div className="mt-12 grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-16">
              <motion.p
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.7, delay: 0.75 }}
                className="lg:col-span-7 text-lg md:text-xl text-[#01080E]/75 leading-[1.55] max-w-2xl"
              >
                {CAREERS.intro.body}
              </motion.p>

              {/* Workplace card */}
              <motion.aside
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.7, delay: 0.9 }}
                data-testid="careers-workplace-card"
                className="lg:col-span-5 relative bg-[#01080E] text-white p-7 md:p-8"
              >
                <div aria-hidden className="absolute inset-0 grid-texture-dark opacity-30 pointer-events-none" />
                <div className="relative">
                  <div className="flex items-center justify-between mb-6">
                    <span className="font-mono-tech text-[10px] uppercase tracking-[0.25em] text-[#7BDCB5]">
                      / Where you will work
                    </span>
                    <span className="font-mono-tech text-[10px] uppercase tracking-[0.25em] text-white/40">
                      IN.PNQ
                    </span>
                  </div>
                  <ul className="space-y-4 text-sm">
                    <WorkRow icon={MapPin} label="Location" value={CAREERS.workplace.location} />
                    <WorkRow icon={CalendarDays} label="Working days" value={CAREERS.workplace.workingDays} />
                    <WorkRow icon={Building2} label="Setup" value={CAREERS.workplace.setup} />
                    <WorkRow icon={Clock} label="Hours" value={CAREERS.workplace.hours} />
                  </ul>
                  <button
                    type="button"
                    onClick={() => applyFor("")}
                    data-testid="careers-hero-apply"
                    className="mt-8 group inline-flex items-center gap-2 bg-[#2AC0A5] text-[#01080E] font-semibold px-5 py-3 hover:bg-white transition-colors"
                  >
                    Jump to application
                    <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
                  </button>
                </div>
              </motion.aside>
            </div>
          </div>
        </section>

        {/* ============================ MARQUEE ============================ */}
        <section
          data-testid="careers-marquee"
          aria-hidden="true"
          className="border-y border-[#01080E]/10 bg-white py-6 md:py-8 overflow-hidden"
        >
          <motion.div
            className="flex whitespace-nowrap gap-14 will-change-transform"
            animate={{ x: ["0%", "-50%"] }}
            transition={{ duration: 42, ease: "linear", repeat: Infinity }}
          >
            {[...marquee, ...marquee, ...marquee].map((m, i) => (
              <span
                key={i}
                className="font-display text-3xl md:text-5xl font-black tracking-[-0.01em] text-[#01080E]/85"
              >
                {m}
                <span className="text-[#2AC0A5] mx-8">/</span>
              </span>
            ))}
          </motion.div>
        </section>

        {/* ============================ MANIFESTO CHAPTERS ============================ */}
        <section className="py-24 md:py-32 bg-white">
          <div className="max-w-7xl mx-auto px-6 md:px-12">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-16 mb-14 md:mb-20">
              <div className="lg:col-span-4">
                <span className="font-mono-tech text-[11px] uppercase tracking-[0.28em] text-[#01080E]/60">
                  / 001 · Manifesto
                </span>
                <h2 className="mt-4 font-display text-4xl md:text-5xl font-extrabold tracking-[-0.02em] leading-[1.02]">
                  How we hire.
                </h2>
              </div>
              <p className="lg:col-span-8 text-lg md:text-xl text-[#01080E]/75 leading-[1.55] max-w-3xl">
                Three commitments we make to every operator who joins Thoratech. They are the
                same commitments we make to the clients whose numbers you will own.
              </p>
            </div>

            <div className="space-y-px bg-[#01080E]/10 border-y border-[#01080E]/10">
              {CAREERS.principles.map((p, i) => (
                <motion.div
                  key={p.n}
                  initial={{ opacity: 0, y: 24 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-80px" }}
                  transition={{ duration: 0.6, delay: i * 0.08 }}
                  data-testid={`careers-principle-${p.n}`}
                  className="group relative bg-white grid grid-cols-12 gap-6 md:gap-10 items-start p-6 md:p-10 hover:bg-[#FAFAF8] transition-colors"
                >
                  <div className="col-span-12 md:col-span-2">
                    <span className="font-mono-tech text-4xl md:text-6xl font-black text-[#2AC0A5] tracking-tight leading-none">
                      {p.n}
                    </span>
                  </div>
                  <div className="col-span-12 md:col-span-4">
                    <h3 className="font-display text-2xl md:text-3xl font-bold text-[#01080E] leading-tight">
                      {p.t}
                    </h3>
                  </div>
                  <div className="col-span-12 md:col-span-6">
                    <p className="text-base md:text-lg text-[#01080E]/75 leading-relaxed">
                      {p.d}
                    </p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* ============================ OPENINGS ============================ */}
        <section id="openings" className="py-24 md:py-32 bg-[#F7F5F0]">
          <div className="max-w-7xl mx-auto px-6 md:px-12">
            <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-14 md:mb-20">
              <div>
                <span className="font-mono-tech text-[11px] uppercase tracking-[0.28em] text-[#01080E]/60">
                  / 002 · Open roles
                </span>
                <h2 className="mt-4 font-display text-4xl md:text-6xl font-extrabold tracking-[-0.02em] leading-[1.02]">
                  We&apos;re hiring.
                </h2>
              </div>
              <p className="max-w-md text-base md:text-lg text-[#01080E]/70 leading-relaxed">
                Five openings at our Balewadi, Pune operations centre. Roles open on a rolling
                basis. Apply once. One form, one resume. Pick the role that fits you.
              </p>
            </div>

            <div className="border-t border-[#01080E]/15">
              {CAREERS.openings.map((role, i) => {
                const isOpen = openRoleId === role.id;
                return (
                  <motion.div
                    key={role.id}
                    initial={{ opacity: 0, y: 16 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-80px" }}
                    transition={{ duration: 0.55, delay: i * 0.05 }}
                    data-testid={`careers-role-${role.id}`}
                    className="border-b border-[#01080E]/15"
                  >
                    <button
                      type="button"
                      onClick={() => setOpenRoleId(isOpen ? null : role.id)}
                      data-testid={`careers-role-toggle-${role.id}`}
                      className="w-full text-left py-8 md:py-10 grid grid-cols-12 gap-4 md:gap-8 items-baseline group"
                    >
                      <span className="col-span-2 md:col-span-1 font-mono-tech text-sm md:text-base text-[#01080E]/50">
                        0{i + 1}
                      </span>
                      <div className="col-span-10 md:col-span-7">
                        <h3 className="font-display text-2xl md:text-4xl font-extrabold tracking-[-0.01em] text-[#01080E] group-hover:text-[#2AC0A5] transition-colors">
                          {role.title}
                        </h3>
                        <p className="mt-2 font-mono-tech text-xs uppercase tracking-[0.22em] text-[#01080E]/55">
                          {role.type}
                        </p>
                      </div>
                      <div className="hidden md:block md:col-span-3 text-sm text-[#01080E]/70 leading-relaxed">
                        {role.summary.length > 120
                          ? role.summary.slice(0, 118) + "…"
                          : role.summary}
                      </div>
                      <div className="col-span-12 md:col-span-1 flex md:justify-end">
                        <span
                          className={`inline-flex items-center justify-center w-11 h-11 border border-[#01080E]/20 text-[#01080E] group-hover:bg-[#01080E] group-hover:text-white group-hover:border-[#01080E] transition-all ${
                            isOpen ? "bg-[#01080E] text-white border-[#01080E]" : ""
                          }`}
                          aria-hidden="true"
                        >
                          {isOpen ? <X size={18} /> : <ArrowUpRight size={18} />}
                        </span>
                      </div>
                    </button>

                    <motion.div
                      initial={false}
                      animate={{ height: isOpen ? "auto" : 0, opacity: isOpen ? 1 : 0 }}
                      transition={{ duration: 0.45, ease: [0.16, 1, 0.3, 1] }}
                      className="overflow-hidden"
                    >
                      <div className="pb-10 md:pb-14 grid grid-cols-12 gap-4 md:gap-8">
                        <div className="col-span-12 md:col-start-2 md:col-span-6">
                          <p className="text-base md:text-lg text-[#01080E]/80 leading-relaxed">
                            {role.summary}
                          </p>
                          <h4 className="mt-8 font-mono-tech text-[11px] uppercase tracking-[0.25em] text-[#01080E]/60">
                            / What you will own
                          </h4>
                          <ul className="mt-3 space-y-2">
                            {role.responsibilities.map((r) => (
                              <li key={r} className="flex gap-3 text-sm md:text-base text-[#01080E]/80">
                                <span className="mt-2 block w-1.5 h-1.5 bg-[#2AC0A5] flex-shrink-0" />
                                <span>{r}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                        <div className="col-span-12 md:col-span-4">
                          <h4 className="font-mono-tech text-[11px] uppercase tracking-[0.25em] text-[#01080E]/60">
                            / Must haves
                          </h4>
                          <ul className="mt-3 space-y-2">
                            {role.musthaves.map((m) => (
                              <li key={m} className="flex gap-3 text-sm md:text-base text-[#01080E]/80">
                                <Check size={14} className="mt-1.5 text-[#2AC0A5] flex-shrink-0" />
                                <span>{m}</span>
                              </li>
                            ))}
                          </ul>
                          <button
                            type="button"
                            data-testid={`careers-apply-${role.id}`}
                            onClick={() => applyFor(role.title)}
                            className="mt-8 group inline-flex items-center gap-2 bg-[#01080E] text-white font-semibold px-6 py-3.5 hover:bg-[#2AC0A5] hover:text-[#01080E] transition-colors"
                          >
                            Apply for this role
                            <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
                          </button>
                        </div>
                      </div>
                    </motion.div>
                  </motion.div>
                );
              })}
            </div>
          </div>
        </section>

        {/* ============================ APPLY FORM ============================ */}
        <section id="apply" className="py-24 md:py-32 bg-[#01080E] text-white relative overflow-hidden">
          <div aria-hidden className="absolute inset-0 grid-texture-dark opacity-40 pointer-events-none" />
          <motion.div
            aria-hidden
            className="absolute -bottom-40 -left-40 w-[600px] h-[600px] rounded-full blur-[140px] bg-[#2AC0A5]/25"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            transition={{ duration: 1.2 }}
          />
          <div className="relative max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
            <div className="lg:col-span-5">
              <span className="font-mono-tech text-[11px] uppercase tracking-[0.28em] text-[#7BDCB5]">
                / 003 · Apply
              </span>
              <h2 className="mt-4 font-display text-4xl md:text-6xl font-extrabold tracking-[-0.02em] leading-[1.02]">
                One form.
                <br />
                <span className="text-[#2AC0A5]">One resume.</span>
              </h2>
              <p className="mt-6 text-lg text-white/75 leading-relaxed max-w-md">
                Attach your CV (PDF, DOC or DOCX, up to 5 MB). We store it securely and review
                every application carefully.
              </p>
              <ul className="mt-10 space-y-3 text-sm text-white/70">
                <li className="flex gap-3">
                  <Check size={16} className="mt-1 text-[#2AC0A5] flex-shrink-0" />
                  <span>Your resume is stored on Thoratech infrastructure, not on public storage.</span>
                </li>
                <li className="flex gap-3">
                  <Check size={16} className="mt-1 text-[#2AC0A5] flex-shrink-0" />
                  <span>You will hear back from a real person, usually the engagement lead for the role.</span>
                </li>
                <li className="flex gap-3">
                  <Check size={16} className="mt-1 text-[#2AC0A5] flex-shrink-0" />
                  <span>No aptitude gates, no gimmicks. Just a conversation about operations you have run.</span>
                </li>
              </ul>
            </div>

            <div className="lg:col-span-7">
              <ApplicationForm
                selectedRole={selectedRole}
                setSelectedRole={setSelectedRole}
              />
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}

// ---------------- Sub-components ----------------

function WorkRow({ icon: Icon, label, value }) {
  return (
    <li className="flex items-start gap-4">
      <span className="mt-0.5 flex-shrink-0 w-8 h-8 border border-white/15 flex items-center justify-center">
        <Icon size={14} className="text-[#2AC0A5]" />
      </span>
      <div>
        <div className="font-mono-tech text-[10px] uppercase tracking-[0.22em] text-white/50">
          {label}
        </div>
        <div className="mt-0.5 text-white/95 font-medium">{value}</div>
      </div>
    </li>
  );
}

function ApplicationForm({ selectedRole, setSelectedRole }) {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    experience: "",
    cover_note: "",
  });
  const [file, setFile] = useState(null);
  const [fileError, setFileError] = useState("");
  const [state, setState] = useState({ status: "idle", error: "" });

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const validateFile = (f) => {
    if (!f) return "Please attach your resume.";
    const nameLc = (f.name || "").toLowerCase();
    const okExt = [".pdf", ".doc", ".docx"].some((e) => nameLc.endsWith(e));
    const okMime = !f.type || ACCEPT_MIME.includes(f.type) || okExt;
    if (!okExt && !okMime) return "Resume must be a PDF, DOC or DOCX file.";
    if (f.size > MAX_BYTES) return "Resume must be 5 MB or less.";
    if (f.size === 0) return "That file appears to be empty.";
    return "";
  };

  const onFileChange = (e) => {
    const f = e.target.files?.[0] || null;
    setFile(f);
    setFileError(validateFile(f));
  };

  const roleOptions = useMemo(
    () => [...CAREERS.openings.map((r) => r.title), "Other"],
    []
  );

  const onSubmit = async (e) => {
    e.preventDefault();
    if (state.status === "loading") return;

    const err = validateFile(file);
    if (err) {
      setFileError(err);
      return;
    }
    if (!selectedRole) {
      setState({ status: "error", error: "Please pick a role you are applying for." });
      return;
    }

    setState({ status: "loading", error: "" });

    const fd = new FormData();
    fd.append("name", form.name.trim());
    fd.append("email", form.email.trim());
    fd.append("phone", form.phone.trim());
    fd.append("role", selectedRole);
    fd.append("experience", form.experience.trim());
    fd.append("cover_note", form.cover_note.trim());
    fd.append("website", ""); // honeypot
    fd.append("resume", file);

    try {
      const res = await fetch(`${API_BASE}/api/applications`, {
        method: "POST",
        body: fd,
      });
      if (!res.ok) {
        let msg = SUBMIT_FALLBACK_MESSAGE;
        try {
          const j = await res.json();
          msg = readableApiError(j);
        } catch (_e) {
          /* body may not be JSON */
        }
        setState({ status: "error", error: msg });
        return;
      }
      // Fire GTM event only after confirmed success. No PII.
      trackApplicationSubmit();
      setState({ status: "success", error: "" });
      setForm({ name: "", email: "", phone: "", experience: "", cover_note: "" });
      setFile(null);
      setSelectedRole("");
    } catch (_e) {
      setState({
        status: "error",
        error: SUBMIT_FALLBACK_MESSAGE,
      });
    }
  };

  if (state.status === "success") {
    return (
      <div
        data-testid="careers-success"
        className="bg-white/5 border border-[#2AC0A5]/40 p-8 md:p-10 backdrop-blur-sm"
      >
        <div className="w-12 h-12 bg-[#2AC0A5] text-[#01080E] flex items-center justify-center mb-6">
          <Check size={22} />
        </div>
        <h3 className="font-display text-3xl md:text-4xl font-extrabold text-white tracking-[-0.01em]">
          Thank you.
        </h3>
        <p className="mt-4 text-white/80 text-base md:text-lg leading-relaxed max-w-lg">
          Your application has been received successfully. Our team will review your profile
          and contact you as soon as possible if your experience matches the current
          requirement.
        </p>
        <button
          type="button"
          data-testid="careers-success-reset"
          onClick={() => setState({ status: "idle", error: "" })}
          className="mt-8 inline-flex items-center gap-2 bg-[#2AC0A5] text-[#01080E] font-semibold px-6 py-3.5 hover:bg-white transition-colors"
        >
          Submit another application
          <ArrowRight size={16} />
        </button>
      </div>
    );
  }

  return (
    <form
      data-testid="careers-form"
      onSubmit={onSubmit}
      noValidate
      className="bg-white/[0.04] border border-white/10 backdrop-blur-sm p-7 md:p-10"
    >
      <div className="flex items-center justify-between mb-8">
        <span className="font-mono-tech text-[10px] uppercase tracking-[0.25em] text-[#7BDCB5]">
          / Job application
        </span>
        <span className="font-mono-tech text-[10px] uppercase tracking-[0.25em] text-white/40">
          v2 · secure upload
        </span>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
        <Field label="Full name" testId="careers-name" required value={form.name} onChange={update("name")} autoComplete="name" />
        <Field label="Email" type="email" testId="careers-email" required value={form.email} onChange={update("email")} autoComplete="email" />
        <Field label="Phone" type="tel" testId="careers-phone" required value={form.phone} onChange={update("phone")} autoComplete="tel" />
        <Field label="Years of experience" testId="careers-experience" value={form.experience} onChange={update("experience")} placeholder="e.g. 3 years" />
      </div>

      <div className="mt-6">
        <label className="font-mono-tech text-[10px] uppercase tracking-[0.25em] text-white/55">
          Role applying for <span className="text-[#2AC0A5]">*</span>
        </label>
        <select
          required
          data-testid="careers-role"
          value={selectedRole}
          onChange={(e) => setSelectedRole(e.target.value)}
          className="mt-2 w-full bg-transparent border-b border-white/20 focus:border-[#2AC0A5] outline-none text-white py-3 transition-colors"
        >
          <option value="" className="bg-[#01080E]">
            Select a role
          </option>
          {roleOptions.map((r) => (
            <option key={r} value={r} className="bg-[#01080E]">
              {r}
            </option>
          ))}
        </select>
      </div>

      <div className="mt-6">
        <label className="font-mono-tech text-[10px] uppercase tracking-[0.25em] text-white/55">
          Cover note
        </label>
        <textarea
          data-testid="careers-cover-note"
          rows={5}
          value={form.cover_note}
          onChange={update("cover_note")}
          maxLength={2000}
          placeholder="Tell us about the operations you have run and what kind of role you are looking for…"
          className="mt-2 w-full bg-transparent border-b border-white/20 focus:border-[#2AC0A5] outline-none text-white placeholder:text-white/30 py-3 resize-none transition-colors"
        />
      </div>

      {/* Honeypot: hidden from users, spammers fill it */}
      <input
        type="text"
        name="website"
        tabIndex={-1}
        autoComplete="off"
        aria-hidden="true"
        className="hidden"
        onChange={() => {}}
      />

      {/* File upload */}
      <div className="mt-8">
        <label className="font-mono-tech text-[10px] uppercase tracking-[0.25em] text-white/55">
          Resume / CV <span className="text-[#2AC0A5]">*</span>
        </label>
        <label
          htmlFor="careers-resume-input"
          className={`mt-2 flex flex-col sm:flex-row sm:items-center gap-4 border border-dashed p-5 cursor-pointer transition-colors ${
            fileError
              ? "border-red-400/60 bg-red-500/5"
              : file
              ? "border-[#2AC0A5]/60 bg-[#2AC0A5]/5"
              : "border-white/25 hover:border-[#2AC0A5]/60 bg-white/[0.02]"
          }`}
        >
          <span className="flex-shrink-0 w-11 h-11 border border-white/15 flex items-center justify-center">
            <Paperclip size={16} className="text-[#2AC0A5]" />
          </span>
          <div className="flex-1 min-w-0">
            {file ? (
              <>
                <div className="text-white text-sm font-medium truncate">{file.name}</div>
                <div className="text-white/50 text-xs mt-1">
                  {(file.size / 1024).toFixed(1)} KB · click to replace
                </div>
              </>
            ) : (
              <>
                <div className="text-white text-sm font-medium">Attach your resume</div>
                <div className="text-white/50 text-xs mt-1">
                  PDF, DOC or DOCX · Max 5 MB
                </div>
              </>
            )}
          </div>
          <input
            id="careers-resume-input"
            data-testid="careers-resume"
            type="file"
            accept={ACCEPT_EXT}
            onChange={onFileChange}
            className="sr-only"
          />
        </label>
        {fileError && (
          <div
            data-testid="careers-file-error"
            className="mt-2 flex items-center gap-2 text-red-300 text-xs"
          >
            <AlertCircle size={14} /> {fileError}
          </div>
        )}
      </div>

      {state.status === "error" && (
        <div
          data-testid="careers-form-error"
          className="mt-6 flex items-start gap-3 border border-red-400/40 bg-red-500/10 p-4"
        >
          <AlertCircle size={18} className="text-red-300 mt-0.5 flex-shrink-0" />
          <p className="text-sm text-white/90">{state.error}</p>
        </div>
      )}

      <div className="mt-8 flex flex-wrap items-center justify-between gap-4">
        <p className="text-xs text-white/50 max-w-sm">
          By submitting, you consent to Thoratech storing your application and contacting
          you about this role. See our{" "}
          <Link to="/privacy" className="underline underline-offset-2 hover:text-[#2AC0A5]">
            privacy policy
          </Link>
          .
        </p>
        <button
          type="submit"
          disabled={state.status === "loading"}
          data-testid="careers-submit"
          className="group inline-flex items-center gap-2 bg-[#2AC0A5] text-[#01080E] font-semibold px-6 py-3.5 hover:bg-white transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
        >
          {state.status === "loading" ? (
            <>
              <Loader2 size={18} className="animate-spin" />
              Submitting…
            </>
          ) : (
            <>
              Send application
              <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
            </>
          )}
        </button>
      </div>

      <p className="mt-6 font-mono-tech text-[10px] uppercase tracking-[0.22em] text-white/40">
        Applications go to {BRAND.contact.email}
      </p>
    </form>
  );
}

function Field({ label, type = "text", value, onChange, required, testId, autoComplete, placeholder }) {
  return (
    <div>
      <label className="font-mono-tech text-[10px] uppercase tracking-[0.25em] text-white/55">
        {label}
        {required && <span className="text-[#2AC0A5]"> *</span>}
      </label>
      <input
        type={type}
        required={required}
        value={value}
        onChange={onChange}
        autoComplete={autoComplete}
        placeholder={placeholder}
        data-testid={testId}
        className="mt-2 w-full bg-transparent border-b border-white/20 focus:border-[#2AC0A5] outline-none text-white placeholder:text-white/30 py-3 transition-colors"
      />
    </div>
  );
}
